import {
  duplicateParts,
  editFirstTierSupplier,
  getFirstTierSuppliers,
  getPartsBySupplier,
  getPartSKPBySupplier,
  insertPartToSupplier,
  updatePart,
  type duplicatePartPayload,
  type EditFirstTierSupplierPayload,
  type getfirstTierParams,
  type partSKP,
} from "@/api/supplierService";
import {
  importPart,
  type ImportPartBody,
  importPartV1,
  type ImportPartV1Body,
} from "@/api/importPartService";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useQueryInvalidation } from "@/lib/queryInvalidation";

export const useGetFirstTierSuppliers = (params: getfirstTierParams) => {
  const query = useQuery({
    queryKey: queryKeys.firstTierSupplier.list(),
    queryFn: () => getFirstTierSuppliers(params),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

  return query;
};

export const useEditFirstTierSupplier = () => {
  const invalidate = useQueryInvalidation();

  const mutatation = useMutation({
    mutationKey: queryKeys.firstTierSupplier.edit(),
    mutationFn: (data: {
      supplierCode: string;
      payload: EditFirstTierSupplierPayload;
    }) => editFirstTierSupplier(data.supplierCode, data.payload),
    onSuccess: () => {
      invalidate.invalidateFirstTierSuppliers();
    },
  });

  return mutatation;
};

export const useGetPartBySupplier = (
  supplierCode: string,
  version: string,
  isActive?: boolean,
) => {
  const query = useQuery({
    queryKey: queryKeys.firstTierSupplier.partList(
      supplierCode,
      version,
      isActive,
    ),
    queryFn: () => getPartsBySupplier(supplierCode, version, isActive),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!supplierCode,
  });

  return query;
};

export const useGetPartSKPBySupplier = (supplierCode: string) => {
  const query = useQuery({
    queryKey: queryKeys.firstTierSupplier.partSKPList(supplierCode),
    queryFn: () => getPartSKPBySupplier(supplierCode),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: !!supplierCode,
  });

  return query;
};

export const useInsertPartToSupplier = () => {
  const invalidate = useQueryInvalidation();

  const mutation = useMutation({
    mutationKey: queryKeys.firstTierSupplier.insertPart(),
    mutationFn: (data: { supplierCode: string; payload: partSKP }) =>
      insertPartToSupplier(data.supplierCode, data.payload),
    onSuccess: () => {
      invalidate.invalidatePart();
    },
  });

  return mutation;
};

export const useUpdatePart = () => {
  const invalidate = useQueryInvalidation();

  const mutation = useMutation({
    mutationKey: queryKeys.firstTierSupplier.updatePart(),
    mutationFn: ({
      partIds,
      isActive,
    }: {
      partIds: Array<number>;
      isActive: boolean;
    }) => updatePart(partIds, isActive),
    onSuccess: () => {
      invalidate.invalidatePart();
    },
  });

  return mutation;
};

export const useDuplicateParts = () => {
  const invalidate = useQueryInvalidation();

  const mutation = useMutation({
    mutationKey: queryKeys.firstTierSupplier.duplicateParts(),
    mutationFn: (data: {
      supplierCode: string;
      payload: duplicatePartPayload[];
    }) => duplicateParts(data.supplierCode, data.payload),
    onSuccess: () => {
      invalidate.invalidatePart();
    },
  });

  return mutation;
};

export const useImportPart = () => {
  const invalidate = useQueryInvalidation();

  return useMutation({
    mutationFn: (data: { body: ImportPartBody; supplierCode: string }) =>
      importPart(data.body, data.supplierCode),
    onSuccess: () => {
      invalidate.invalidateImportPart();
    },
  });
};

export const useImportPartV1 = () => {
  const invalidate = useQueryInvalidation();

  return useMutation({
    mutationFn: (data: { body: ImportPartV1Body; supplierCode: string }) =>
      importPartV1(data.body, data.supplierCode),
    onSuccess: () => {
      invalidate.invalidateImportPartV1();
    },
  });
};
